/**
 * v3.carts — /v3/carts/* (8 routes).
 *
 * All routes are wired to CartFunction. listMy/listUnclaimed return
 * `{ success, data: CartDto[] }`; the rest return `CartStateResult`
 * (`{ success, cart }`).
 */

import type {
  RequestOptions,
  CartStateResult,
  CreateCartPayload,
  UpdateCartPayload,
  CartClaimInput,
  CartArchiveInput,
  CartListFilters,
  CartDto,
  ApiResult,
  ResolveCartInput,
  ResolveCartResponse,
  SweepEmptyFsrDraftsInput,
  SweepEmptyFsrDraftsResponse,
  ComplianceQueueRow,
  ComplianceQueueStatus,
  ComplianceQueueEntityType,
  RestoreCartResponse,
  CartProductResolution,
  CartProductResolutionInput,
  CommitOutcome,
  CommitStatus,
  CommitAbortInput,
  ReviewSummary,
  PipelinePreview,
  CartDeclineInput,
  CartPartyUpsertInput,
  CartLineItemCreateInput,
  CartLineItemPatchInput,
  CartContentCommandResult,
  CartCardsResponse,
  CardActivateInput,
  CardActivateResponse,
  SupervisorOverrideList,
  SupervisorOverrideGrantInput,
  SupervisorOverrideResult,
  SignedPacketsResponse,
} from '@retail-ops/shared-types';
import type { ApiHttpClient } from '../http.js';
import { buildAdminAuthHeaders } from '../admin-auth.js';
import { mapCommitOutcome, mapCommitStatus } from './commitMapping.js';

// -----------------------------------------------------------------------------
// Wave 6.3 — per-product disposition sub-client
// -----------------------------------------------------------------------------

class CartProductResolutionApi {
  public constructor(private readonly http: ApiHttpClient) {}

  /** List per-product dispositions for a cart. */
  public list(
    cartId: string,
    opts?: RequestOptions,
  ): Promise<ApiResult<ReadonlyArray<CartProductResolution>>> {
    return this.http.get<ApiResult<ReadonlyArray<CartProductResolution>>>(
      `/v3/carts/${encodeURIComponent(cartId)}/product-resolutions`,
      { ...opts },
    );
  }

  /** Upsert a per-product disposition. Backend MERGE on (cartId, productCode). */
  public upsert(
    cartId: string,
    body: CartProductResolutionInput,
    opts?: RequestOptions,
  ): Promise<ApiResult<CartProductResolution>> {
    return this.http.post<ApiResult<CartProductResolution>>(
      `/v3/carts/${encodeURIComponent(cartId)}/product-resolutions`,
      { ...opts, body },
    );
  }
}

// -----------------------------------------------------------------------------
// BACKLOG-1787 — supervisor-override sub-client (V3SupervisorOverrideFunction)
// -----------------------------------------------------------------------------

class CartSupervisorOverrideApi {
  public constructor(private readonly http: ApiHttpClient) {}

  /** GET — the cart's override ledger plus whether the signed-in user may grant / revoke. */
  public list(cartId: string, opts?: RequestOptions): Promise<SupervisorOverrideList> {
    return this.http.get<SupervisorOverrideList>(
      `/v3/carts/${encodeURIComponent(cartId)}/supervisor-overrides`,
      { ...opts },
    );
  }

  /**
   * POST — grant. The granter is the signed-in user (never sent in the body). The
   * server refuses: a role outside `override.supervisor.roles`, the cart's own
   * officer, a blank reason, a closed cart, or a second active override.
   */
  public grant(
    cartId: string,
    body: SupervisorOverrideGrantInput,
    opts?: RequestOptions,
  ): Promise<SupervisorOverrideResult> {
    return this.http.post<SupervisorOverrideResult>(
      `/v3/carts/${encodeURIComponent(cartId)}/supervisor-overrides`,
      { ...opts, body },
    );
  }

  /**
   * DELETE — revoke (the row is kept and stamped). The http client sends no body
   * on DELETE, so the optional reason travels as `?reason=`.
   */
  public revoke(
    cartId: string,
    overrideId: string,
    reason?: string,
    opts?: RequestOptions,
  ): Promise<SupervisorOverrideResult> {
    return this.http.del<SupervisorOverrideResult>(
      `/v3/carts/${encodeURIComponent(cartId)}/supervisor-overrides/${encodeURIComponent(overrideId)}`,
      { ...opts, query: reason ? { reason } : undefined },
    );
  }
}

export class CartsApi {
  /**
   * Wave 6.3 — per-product disposition sub-client (CartProductResolution).
   * `client.retail.carts.productResolution.upsert(cartId, ...)`.
   */
  public readonly productResolution: CartProductResolutionApi;

  /**
   * BACKLOG-1787 — supervisor overrides on a cart's gates.
   * `client.retail.carts.supervisorOverrides.grant(cartId, ...)`.
   */
  public readonly supervisorOverrides: CartSupervisorOverrideApi;

  public constructor(private readonly http: ApiHttpClient) {
    this.productResolution = new CartProductResolutionApi(http);
    this.supervisorOverrides = new CartSupervisorOverrideApi(http);
  }

  public create(req: CreateCartPayload, opts?: RequestOptions): Promise<CartStateResult> {
    return this.http.post<CartStateResult>('/v3/carts', { ...opts, body: req });
  }

  public get(cartId: string, opts?: RequestOptions): Promise<CartStateResult> {
    return this.http.get<CartStateResult>(`/v3/carts/${encodeURIComponent(cartId)}`, { ...opts });
  }

  /**
   * Manual Aurora bridge (Bill 2026-07-02) — download the cart's regulated
   * artifacts (signed packages, imported application) as one zip + manifest
   * for manual Aurora filing. Returns a Blob for a client-side download link.
   * 404 NO_ARTIFACTS until signing completes; 409 MANUAL_ZIP_DISABLED once
   * automatic Aurora delivery is switched on.
   */
  public auroraPackage(cartId: string, opts?: RequestOptions): Promise<Blob> {
    return this.http.getBlob(`/v3/carts/${encodeURIComponent(cartId)}/aurora-package`, { ...opts });
  }

  /**
   * BACKLOG-1298 — the CUSTOMER'S copy: one PDF with the signed documents (and the
   * online application) followed by the disclosures that need no signature, for the
   * officer to print and hand over or mail. Never ID scans or bank records. Errors:
   * 409 NOT_SIGNED / NO_DISCLOSURES, 502 COPY_INCOMPLETE (a document couldn't load).
   */
  /** Signed packets from opening for one customer (Bill 2026-10-17). */
  public signedPacketsForCustomer(cif: string, opts?: RequestOptions): Promise<SignedPacketsResponse> {
    return this.http.get<SignedPacketsResponse>(`/v3/customers/${encodeURIComponent(cif)}/signed-packets`, { ...opts });
  }

  /** The latest signed packets across customers (Documents page before a customer is chosen). */
  public recentSignedPackets(take = 20, opts?: RequestOptions): Promise<SignedPacketsResponse> {
    return this.http.get<SignedPacketsResponse>('/v3/signed-packets/recent', { ...opts, query: { take } });
  }

  /** One document from a packet, as a PDF (streamed by the server; never a SharePoint link). */
  public signedDocumentBlob(cartId: string, ref: number, opts?: RequestOptions): Promise<Blob> {
    return this.http.getBlob(`/v3/carts/${encodeURIComponent(cartId)}/signed-packet/${ref}/content`, { ...opts });
  }

  public customerCopy(cartId: string, opts?: RequestOptions): Promise<Blob> {
    return this.http.getBlob(`/v3/carts/${encodeURIComponent(cartId)}/aurora-package`, {
      ...opts,
      query: { copy: 'customer' },
    });
  }

  /**
   * Debit cards the post-sign pipeline issued for this cart (SPFx
   * CardIssuancePanel port, Collette 3/27/2026 workflow, Bill 2026-07-10).
   * Masked PANs only — the full PAN never exits the Function (PCI,
   * Bill 2026-05-08). Backed by V3CardIssuanceFunction.
   */
  public cards(cartId: string, opts?: RequestOptions): Promise<CartCardsResponse> {
    return this.http.get<CartCardsResponse>(
      `/v3/carts/${encodeURIComponent(cartId)}/cards`,
      { ...opts },
    );
  }

  /**
   * Workflow step 4 (Collette 3/27/2026) — after the FSR prints the warm
   * card in Entrust, flip it Active via EFTCardMod (server resolves the
   * PAN; limits $510/$510 minor, $1,010/$5,010 adult per Collette
   * 4/7/2026). Also auto-completes the cart's 'debit-card-print'
   * follow-up task(s). ADMIN-AUTH-HARDENED (role 'Officer') — pass ''
   * as bypassKey to ride the bearer token.
   */
  public activateCard(
    bypassKey: string,
    cartId: string,
    body: CardActivateInput,
    opts?: RequestOptions,
  ): Promise<CardActivateResponse> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.post<CardActivateResponse>(
      `/v3/carts/${encodeURIComponent(cartId)}/cards/activate`,
      { ...opts, headers, body },
    );
  }

  public update(
    cartId: string,
    req: UpdateCartPayload,
    opts?: RequestOptions
  ): Promise<CartStateResult> {
    return this.http.patch<CartStateResult>(`/v3/carts/${encodeURIComponent(cartId)}`, {
      ...opts,
      body: req,
    });
  }

  /**
   * Current officer's in-progress carts.  Officer identity is resolved
   * server-side from the `x-pcb-officer-code` header that ApiClientContext
   * injects from AuthContext.  Excludes committed/archived by default;
   * supports `status`, `top`, `branch` query filters.
   *
   * Ports SPFx SessionService.listResumableSessions
   * (spfx-account-opening-v2 src/.../services/SessionService.ts:270-323).
   */
  public listMy(
    filters?: CartListFilters,
    opts?: RequestOptions
  ): Promise<ApiResult<CartDto[]>> {
    // The server reads `all` as the literal "1" (V3CartFunction.ListMy); a
    // boolean serialises as "true" and was silently ignored, so "Show all
    // (pilot)" showed nothing but your own carts (Bill 2026-09-11).
    const query: Record<string, string | number | boolean> = filters
      ? { ...(filters as unknown as Record<string, string | number | boolean>) }
      : {};
    if (query.all === true) query.all = 1;
    else if (query.all === false) delete query.all;
    return this.http.get<ApiResult<CartDto[]>>('/v3/carts/my', {
      ...opts,
      ...(Object.keys(query).length > 0 ? { query } : {}),
    });
  }

  /**
   * Supervisor queue: carts with no live lock and not in a terminal state.
   * Oldest-first ordering surfaces SLA pressure naturally.
   */
  public listUnclaimed(
    filters?: CartListFilters,
    opts?: RequestOptions
  ): Promise<ApiResult<CartDto[]>> {
    return this.http.get<ApiResult<CartDto[]>>('/v3/carts/unclaimed', {
      ...opts,
      ...(filters ? { query: filters as unknown as Record<string, string | number | boolean> } : {}),
    });
  }

  public claim(
    cartId: string,
    req: CartClaimInput,
    opts?: RequestOptions
  ): Promise<CartStateResult> {
    return this.http.post<CartStateResult>(`/v3/carts/${encodeURIComponent(cartId)}/claim`, {
      ...opts,
      body: req,
    });
  }

  public unclaim(cartId: string, opts?: RequestOptions): Promise<CartStateResult> {
    return this.http.post<CartStateResult>(`/v3/carts/${encodeURIComponent(cartId)}/unclaim`, {
      ...opts,
      body: {},
    });
  }

  /**
   * Hand a cart to another officer (Bill 2026-07-27). Supervisor / Manager /
   * Admin only — the server enforces the role and derives the receiving
   * officer's officer code AND branch from their provisioned record, so the
   * caller cannot park a cart in an arbitrary branch.
   */
  public reassign(
    cartId: string,
    req: { toOfficerCode: string; toUser?: string; reason?: string },
    opts?: RequestOptions
  ): Promise<CartStateResult> {
    return this.http.post<CartStateResult>(`/v3/carts/${encodeURIComponent(cartId)}/reassign`, {
      ...opts,
      body: req,
    });
  }

  /** Active officers for the reassign picker. */
  public officers(opts?: RequestOptions): Promise<{
    success: boolean;
    officers: Array<{ officerCode: string; displayName: string; branchCode?: string; email?: string }>;
    count: number;
  }> {
    return this.http.get('/v3/config/officers', { ...opts });
  }

  /**
   * Bill 2026-10-15 — append one note (server-side, any status but archived). Replaces saving notes
   * through update(), which sent the whole metadata blob and was refused once the cart was locked
   * for signing. Backend: CartFunction.AddNote (POST /v3/carts/{id}/notes).
   */
  public addNote(
    cartId: string,
    req: { text: string; author?: string },
    opts?: RequestOptions,
  ): Promise<{ success: boolean; cart?: CartDto; error?: string }> {
    return this.http.post(`/v3/carts/${encodeURIComponent(cartId)}/notes`, { ...opts, body: req });
  }

  public archive(
    cartId: string,
    req: CartArchiveInput,
    opts?: RequestOptions
  ): Promise<CartStateResult> {
    // Wire contract is archiveReason / archiveReasonDetails (V3CartFunction
    // CartArchiveInput); the typed input keeps the friendlier reason/details.
    const body: Record<string, unknown> = { archiveReason: req.reason };
    const details = req.details ?? req.notes;
    if (details) body.archiveReasonDetails = details;
    return this.http.post<CartStateResult>(`/v3/carts/${encodeURIComponent(cartId)}/archive`, {
      ...opts,
      body,
    });
  }

  /**
   * Convenience alias: archive the cart with `reason='Completed'`.
   * Used by the RAO commit pipeline on terminal success (matches the SPFx
   * "Save & Close" behavior) so callers don't have to import ArchiveReason.
   */
  public close(
    cartId: string,
    details?: string,
    opts?: RequestOptions
  ): Promise<CartStateResult> {
    const body: CartArchiveInput = details
      ? { reason: 'Completed', details }
      : { reason: 'Completed' };
    return this.archive(cartId, body, opts);
  }

  /**
   * Convenience alias: archive the cart with `reason='Abandoned'`.
   * Populates the Kill List (RKL) downstream.
   */
  public abandon(
    cartId: string,
    details?: string,
    opts?: RequestOptions
  ): Promise<CartStateResult> {
    const body: CartArchiveInput = details
      ? { reason: 'Abandoned', details }
      : { reason: 'Abandoned' };
    return this.archive(cartId, body, opts);
  }

  // ---------------------------------------------------------------------------
  // Tier 2 — Review-stage SPFx-aligned methods (Bill 2026-05-12 Round 3).
  // ---------------------------------------------------------------------------

  /**
   * GET /v3/carts/{cartId}/review-summary — Aggregate review payload:
   * configured chips + per-(subtype × gate) resolved strategies + count of
   * pending deferred-gate trackings. Powers Screen 26's aggregate chip row
   * plus the per-subtype gate badges on the read-only roll-up.
   */
  public async reviewSummary(
    cartId: string,
    opts?: RequestOptions,
  ): Promise<ApiResult<ReviewSummary>> {
    // 2026-07-02 (live-walk fix) — the endpoint returns the summary FLAT
    // ({success, cartId, chips, subtypeGates, ...}, no `data` wrapper) but the
    // consumers read `r.data`, so a healthy 200 rendered as "review summary
    // unavailable". Normalize both shapes here.
    const raw = await this.http.get<ApiResult<ReviewSummary> & { success: boolean; error?: string }>(
      `/v3/carts/${encodeURIComponent(cartId)}/review-summary`,
      { ...opts },
    );
    if (raw && (raw as ApiResult<ReviewSummary>).data) return raw;
    if (raw && raw.success) {
      const { success, error, ...rest } = raw as Record<string, unknown> & { success: boolean; error?: string };
      const out: ApiResult<ReviewSummary> = { success, data: rest as unknown as ReviewSummary };
      if (error) out.error = error;
      return out;
    }
    return raw;
  }

  /**
   * POST /v3/carts/{cartId}/preview-pipeline — Dry-run gate evaluation.
   * Returns the 12 pipeline stages plus per-subtype findings (wouldBlock /
   * wouldWarn / wouldDefer / officerOverride). FSR clicks "Preview Pipeline"
   * before Submit to see what commit would do.
   */
  public previewPipeline(
    cartId: string,
    opts?: RequestOptions,
  ): Promise<ApiResult<PipelinePreview>> {
    return this.http.post<ApiResult<PipelinePreview>>(
      `/v3/carts/${encodeURIComponent(cartId)}/preview-pipeline`,
      { ...opts, body: {} },
    );
  }

  /**
   * POST /v3/carts/{cartId}/decline — Decline Application affordance.
   * Differs from archive() in that it sets ResolutionCode + ResolvedBy +
   * ResolvedAt for audit, and enforces a curated reason enum
   * ('Adverse' | 'Cancelled' | 'Withdrew').
   */
  public decline(
    cartId: string,
    req: CartDeclineInput,
    opts?: RequestOptions,
  ): Promise<CartStateResult> {
    return this.http.post<CartStateResult>(
      `/v3/carts/${encodeURIComponent(cartId)}/decline`,
      { ...opts, body: req },
    );
  }

  // ---------------------------------------------------------------------------
  // Wave 6.2 — resolution-code workflow
  // ---------------------------------------------------------------------------

  /**
   * Resolve a cart with one of the 5 final taxonomy codes:
   * `Open` | `Processed` | `Withdrawn` | `Adverse-Action` | `Cancelled`.
   *
   * Backend validates the (originRole × hasRegulatedArtifact × resolutionCode)
   * matrix and may reject with `409 LOBBY_TICKET_ACTIVE` or
   * `409 EXTERNAL_COMMIT_LOCK` — caller renders these as inline modal errors.
   */
  public resolve(
    cartId: string,
    body: ResolveCartInput,
    opts?: RequestOptions
  ): Promise<ResolveCartResponse> {
    return this.http.post<ResolveCartResponse>(
      `/v3/carts/${encodeURIComponent(cartId)}/resolve`,
      { ...opts, body },
    );
  }

  /**
   * Sweep the caller's empty fsr-self drafts (no PII + no artifact, older
   * than `olderThanMinutes` — default 60). Pass `countOnly:true` to preview
   * the batch size without mutating any rows. The Function uses
   * `x-pcb-officer-code` to scope the sweep.
   */
  public sweepEmptyFsrDrafts(
    body: SweepEmptyFsrDraftsInput,
    opts?: RequestOptions
  ): Promise<SweepEmptyFsrDraftsResponse> {
    return this.http.post<SweepEmptyFsrDraftsResponse>('/v3/carts/sweep-empty-fsr-drafts', {
      ...opts,
      body,
    });
  }

  /**
   * Compliance queue — Adverse-Action review backlog. Status filter defaults
   * to 'pending' for the unread view. Wave 6.3 adds the optional
   * `entityType` filter so the same surface can serve both cart AA and
   * MaintAction AA reviews.
   */
  public complianceQueue(
    status: ComplianceQueueStatus = 'pending',
    opts?: RequestOptions & { entityType?: ComplianceQueueEntityType },
  ): Promise<ApiResult<ComplianceQueueRow[]>> {
    const { entityType, ...rest } = opts ?? {};
    const query: Record<string, string> = { status };
    if (entityType) query.entityType = entityType;
    return this.http.get<ApiResult<ComplianceQueueRow[]>>('/v3/carts/compliance-queue', {
      ...rest,
      query,
    });
  }

  /**
   * Restore a previously-resolved cart back to `Open`. Returns 410 Gone with
   * `errorCode='RETENTION_WINDOW_EXPIRED'` when past the
   * CartRetentionPolicies window — caller surfaces inline retention guidance.
   */
  public restore(
    cartId: string,
    opts?: RequestOptions
  ): Promise<RestoreCartResponse> {
    return this.http.post<RestoreCartResponse>(
      `/v3/carts/${encodeURIComponent(cartId)}/restore`,
      { ...opts, body: {} },
    );
  }

  // ---------------------------------------------------------------------------
  // Row-store blueprint P2 — cart-content commands
  // (docs/architecture/cart-row-store-blueprint-20260706.md)
  //
  // Targeted row commands replace whole-blob peopleJson/productsJson PATCHes.
  // Every command returns CartContentCommandResult with the full recomposed
  // CartDto, so callers replace local state without a second GET. Conflict:
  // HTTP 409 `errorCode='ROW_VERSION_MISMATCH'` ONLY when expectedRowVersion
  // was provided and stale — commands NEVER require cart-level expectedVersion.
  // ---------------------------------------------------------------------------

  /**
   * POST /v3/carts/{cartId}/parties — add an application party.
   * `input.clientId` is the FE person id ("person-{uuid}"); optional — the
   * server generates one when omitted and correlates via CartParties.ClientId.
   */
  public addParty(
    cartId: string,
    input: CartPartyUpsertInput,
    opts?: RequestOptions,
  ): Promise<CartContentCommandResult> {
    return this.http.post<CartContentCommandResult>(
      `/v3/carts/${encodeURIComponent(cartId)}/parties`,
      { ...opts, body: input },
    );
  }

  /**
   * PATCH /v3/carts/{cartId}/parties/{partyKey} — update a party.
   * `partyKey` = FE clientId ("person-{uuid}") OR the CartParties PartyId GUID.
   * Pass `input.expectedRowVersion` for per-row optimistic concurrency.
   */
  public updateParty(
    cartId: string,
    partyKey: string,
    input: CartPartyUpsertInput,
    opts?: RequestOptions,
  ): Promise<CartContentCommandResult> {
    return this.http.patch<CartContentCommandResult>(
      `/v3/carts/${encodeURIComponent(cartId)}/parties/${encodeURIComponent(partyKey)}`,
      { ...opts, body: input },
    );
  }

  /**
   * DELETE /v3/carts/{cartId}/parties/{partyKey} — remove a party.
   * `partyKey` = FE clientId OR PartyId GUID.
   */
  public removeParty(
    cartId: string,
    partyKey: string,
    opts?: RequestOptions,
  ): Promise<CartContentCommandResult> {
    return this.http.del<CartContentCommandResult>(
      `/v3/carts/${encodeURIComponent(cartId)}/parties/${encodeURIComponent(partyKey)}`,
      { ...opts },
    );
  }

  /**
   * POST /v3/carts/{cartId}/line-items — add a product instance. ALWAYS
   * creates a NEW instance (never upserts by product code) — two Free
   * Checkings = two rows. FE generates `input.instanceId` as
   * `"li-" + crypto.randomUUID()`; server generates one when omitted.
   */
  public addLineItem(
    cartId: string,
    input: CartLineItemCreateInput,
    opts?: RequestOptions,
  ): Promise<CartContentCommandResult> {
    return this.http.post<CartContentCommandResult>(
      `/v3/carts/${encodeURIComponent(cartId)}/line-items`,
      { ...opts, body: input },
    );
  }

  /**
   * PATCH /v3/carts/{cartId}/line-items/{instanceId} — update a product
   * instance (config / ownership / nickname). Legacy items carry
   * `instanceId === productCode` (backfill), so old carts address the same way.
   */
  public updateLineItem(
    cartId: string,
    instanceId: string,
    input: CartLineItemPatchInput,
    opts?: RequestOptions,
  ): Promise<CartContentCommandResult> {
    return this.http.patch<CartContentCommandResult>(
      `/v3/carts/${encodeURIComponent(cartId)}/line-items/${encodeURIComponent(instanceId)}`,
      { ...opts, body: input },
    );
  }

  /**
   * DELETE /v3/carts/{cartId}/line-items/{instanceId} — remove a product
   * instance.
   */
  public removeLineItem(
    cartId: string,
    instanceId: string,
    opts?: RequestOptions,
  ): Promise<CartContentCommandResult> {
    return this.http.del<CartContentCommandResult>(
      `/v3/carts/${encodeURIComponent(cartId)}/line-items/${encodeURIComponent(instanceId)}`,
      { ...opts },
    );
  }

  /**
   * PUT /v3/carts/{cartId}/line-items/{instanceId}/beneficiaries — replace-set
   * the beneficiary entries for one product instance. The
   * `metadataJson.beneficiaries` map is keyed by INSTANCE ID going forward
   * (legacy keys were product codes, which backfill made valid instance ids).
   */
  public setLineItemBeneficiaries(
    cartId: string,
    instanceId: string,
    entries: ReadonlyArray<Record<string, unknown>>,
    opts?: RequestOptions,
  ): Promise<CartContentCommandResult> {
    return this.http.put<CartContentCommandResult>(
      `/v3/carts/${encodeURIComponent(cartId)}/line-items/${encodeURIComponent(instanceId)}/beneficiaries`,
      { ...opts, body: { entries } },
    );
  }

  // ---------------------------------------------------------------------------
  // Wave 7 — Commit pipeline (RAO §3.1 11-step orchestrator)
  //
  // Single entry point: POST /v3/carts/{cartId}/commit. The backend
  // CartCommitService walks steps 1-7 inline, pauses at SignNow dispatch, and
  // resumes via PipelineResumeFunction once `document_group.complete` fires.
  // The UI polls /commit/status every 2s while the cart is in a non-terminal
  // commit state (committing | awaiting-signatures | pipeline-error).
  // ---------------------------------------------------------------------------

  /**
   * Kick off the commit pipeline for a draft/active cart.
   *
   * On success the response carries the canonical post-call `cart` row plus a
   * `status` snapshot so the caller can render the progress bar without a
   * second round-trip. Inline-error codes the UI must humanize:
   *   - `PRECOMMIT_VALIDATION_FAILED` — pre-flight gate fail (e.g. no signers)
   *   - `LOBBY_TICKET_ACTIVE` / `EXTERNAL_COMMIT_LOCK` — same as resolve()
   */
  public async commit(
    cartId: string,
    opts?: RequestOptions,
  ): Promise<CommitOutcome> {
    // The Function wraps this in `{ success, data, error, code }` — unwrap +
    // map to the canonical CommitOutcome (see commitMapping.ts).
    const env = await this.http.post<unknown>(
      `/v3/carts/${encodeURIComponent(cartId)}/commit`,
      { ...opts, body: {} },
    );
    return mapCommitOutcome(env);
  }

  /**
   * Manual retry from a `pipeline-error` state. Re-uses the
   * cart-scoped idempotency keys so successful steps short-circuit and the
   * pipeline resumes from the failing step (per §5).
   */
  public async commitRetry(
    cartId: string,
    opts?: RequestOptions,
  ): Promise<CommitOutcome> {
    const env = await this.http.post<unknown>(
      `/v3/carts/${encodeURIComponent(cartId)}/commit/retry`,
      { ...opts, body: {} },
    );
    return mapCommitOutcome(env);
  }

  /**
   * Bail-out path: aborts an in-flight commit and resolves the cart with the
   * supplied disposition (typically `System-Error` paired with a closed-list
   * errorCode). The CommitErrorPanel uses this in tandem with `CloseCartModal`
   * to keep the resolution-code workflow consistent across resolution surfaces.
   */
  public async commitAbort(
    cartId: string,
    body: CommitAbortInput,
    opts?: RequestOptions,
  ): Promise<CommitOutcome> {
    const env = await this.http.post<unknown>(
      `/v3/carts/${encodeURIComponent(cartId)}/commit/abort`,
      { ...opts, body },
    );
    return mapCommitOutcome(env);
  }

  /**
   * Live status snapshot for the CommitFlow progress bar. The CommitFlow polls
   * this every 2s while the cart is in a non-terminal commit state. Always
   * returns `steps.length === 11` (per §3.1 of the design).
   */
  public async getCommitStatus(
    cartId: string,
    opts?: RequestOptions,
  ): Promise<CommitStatus> {
    // Function wraps the CommitStatusResponse in `{ success, data, error, code }`
    // and uses server field/step names — unwrap + translate to the canonical
    // CommitStatus so `cartStatus`/`steps[].stepKey`/`currentStepKey` are real
    // (was returning the raw envelope → snap.cartStatus undefined → 2s reload
    // loop). See commitMapping.ts.
    const env = await this.http.get<unknown>(
      `/v3/carts/${encodeURIComponent(cartId)}/commit/status`,
      { ...opts },
    );
    return mapCommitStatus(env);
  }
}
